// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';

// Utils
import { isBrowserThemeSupported } from './themeService';

// Actions
import { setThemeMode } from './themeActions';

// Selectors
import { getThemeModeSelector } from './themeSelector';
import { selectIsMobileAppMode } from '../../../../../platform/platformSelector';

// Components
import RowLayout from '../../../../../components/layout/RowLayout';
import ColumnLayout from '../../../../../components/layout/ColumnLayout';
import SettingImageButton from '../../components/SettingImageButton';
import SettingsRow from '../../components/SettingsRow';
import ToggleInput from '../../../../../components/form/fields/ToggleInput';

// Styles
import './ThemeSettingsSection.scss';

// Constants
import { THEMES } from './themeConstants';

// Images
import previewImageDarkMobile from './dark-mode-mobile.png';
import previewImageLightMobile from './light-mode-mobile.png';
import previewImageDark from './dark-mode.png';
import previewImageLight from './light-mode.png';
import mobileOutline from './outline.svg';

const mapStateToProps = createStructuredSelector({
    mode: getThemeModeSelector,
});

const mapDispatchToProps = (dispatch) => ({
    dispatchSetThemeMode: (mode) => dispatch(setThemeMode(mode)),
});

const ThemeSettingsSection = (props) => {
    const { mode, dispatchSetThemeMode } = props;

    const onToggleMatchTheme = (e) => {
        if (e.target.checked) {
            return dispatchSetThemeMode(THEMES.MATCH_BROWSER);
        }
        dispatchSetThemeMode(THEMES.LIGHT);
    };

    const matchBrowserTheme = mode === THEMES.MATCH_BROWSER;
    const isMobile = useSelector(selectIsMobileAppMode);

    return (
        <div className="ThemeSettingsSection">
            <div className="ThemeSettingsPreviewButtons">
                <RowLayout>
                    <ColumnLayout>
                        <SettingImageButton
                            onClick={() => dispatchSetThemeMode(THEMES.LIGHT)}
                            label="Light mode"
                            disabled={matchBrowserTheme}
                            selected={mode === THEMES.LIGHT}
                            imageSrc={isMobile ? previewImageLightMobile : previewImageLight}
                            showUnselected={isMobile}
                            outlineSrc={isMobile && mobileOutline}
                        />
                    </ColumnLayout>
                    <ColumnLayout>
                        <SettingImageButton
                            onClick={() => dispatchSetThemeMode(THEMES.DARK)}
                            label="Dark mode"
                            disabled={matchBrowserTheme}
                            selected={mode === THEMES.DARK}
                            imageSrc={isMobile ? previewImageDarkMobile : previewImageDark}
                            showUnselected={isMobile}
                            outlineSrc={isMobile && mobileOutline}
                        />
                    </ColumnLayout>
                </RowLayout>
            </div>
            {isBrowserThemeSupported() && (
                <SettingsRow
                    label="Match system theme"
                    details="Milanote will choose the theme that matches your operating system"
                    showDetailsOnMobile
                >
                    <ToggleInput active={matchBrowserTheme} onChange={onToggleMatchTheme} />
                </SettingsRow>
            )}
        </div>
    );
};

ThemeSettingsSection.propTypes = {
    mode: PropTypes.string,
    dispatchSetThemeMode: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(ThemeSettingsSection);
